import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip,
  Collapse,
  Grid,
  Divider,
  Snackbar,
  Autocomplete,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Autorenew as RegenerateIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  PhoneAndroid as DeviceIcon,
  Settings as ConfigIcon,
  Phone as PhoneIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  AccessTime as TimeIcon,
  PhoneInTalk as AnsweredIcon,
  CallEnd as EndedIcon,
  LockClock as CredentialIcon,
  PowerOff as DeactivatedIcon,
  HowToReg as ActivatedIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';
import { telephonyApi, devicesApi } from '../../services/apiClient';
import { getDeviceLanIP } from '../../utils/deviceHelpers';
import ProvisionSipDeviceDialog from './ProvisionSipDeviceDialog';
import ProvisionPersonDialog from './ProvisionPersonDialog';
import { generateSipDeviceLabel } from './SipDeviceLabelPdf';

// ─── Constants ───────────────────────────────────────────────────
const ASTERISK_DEFAULTS = {
  server: 'phone.caro.care',
  tlsPort: 5061,
  udpPort: 5060,
  transport: 'TLS',
  realm: 'caro.care',
  codec: 'G.711 \u03BC-law (PCMU)',
};

const CAMERA_STREAM_BASE = 'https://phone.caro.care/stream';

const TRANSPORT_OPTIONS = [
  { value: 'tls', label: 'TLS (Recommended)' },
  { value: 'udp', label: 'UDP (Fallback)' },
];

const CONTEXT_OPTIONS = [
  { value: 'caro-videophone', label: 'Videophone / Entrance Panel' },
  { value: 'caro-internal', label: 'Internal (Resident/Common Area)' },
  { value: 'caro-staff', label: 'Staff' },
  { value: 'caro-emergency', label: 'Emergency' },
];

// SIP session event config for timeline display
const sipEventConfig = {
  created:                  { icon: <TimeIcon fontSize="small" />,         color: '#1565C0', label: 'Session created' },
  activated:                { icon: <ActivatedIcon fontSize="small" />,    color: '#2E7D32', label: 'Activated' },
  deactivated:              { icon: <DeactivatedIcon fontSize="small" />,  color: '#C62828', label: 'Deactivated' },
  registered:               { icon: <AnsweredIcon fontSize="small" />,     color: '#2E7D32', label: 'Device registered' },
  unregistered:             { icon: <EndedIcon fontSize="small" />,        color: '#F57F17', label: 'Device unregistered' },
  credentials_regenerated:  { icon: <CredentialIcon fontSize="small" />,   color: '#7B1FA2', label: 'Credentials regenerated' },
  expired:                  { icon: <EndedIcon fontSize="small" />,        color: '#C62828', label: 'Session expired' },
  registration_timeout:     { icon: <EndedIcon fontSize="small" />,        color: '#E65100', label: 'Registration timeout' },
};

function formatFullDateTime(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

// ─── Helpers for session status ─────────────────────────────────
function getSessionStatusInfo(session) {
  const now = new Date();
  const expires = session.expiresAt ? new Date(session.expiresAt) : null;
  if (!session.isActive || (expires && expires < now)) {
    return { label: 'Expired', bg: '#FFEBEE', color: '#C62828' };
  }
  if (expires) {
    const daysLeft = (expires - now) / 86400000;
    if (daysLeft < 3) return { label: 'Expiring', bg: '#FFF3E0', color: '#E65100' };
  }
  return { label: 'Active', bg: '#E8F5E9', color: '#2E7D32' };
}

// ═════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════
export default function SipDevicesUnifiedTab({ t, isAdmin }) {
  // ─── Data state ──────────────────────────────────────────────
  const [endpoints, setEndpoints] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ─── Filters ─────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'location' | 'person'
  const [activeOnly, setActiveOnly] = useState(false);

  // ─── Table ───────────────────────────────────────────────────
  const [orderBy, setOrderBy] = useState('extension');
  const [order, setOrder] = useState('asc');
  const [expandedRow, setExpandedRow] = useState(null);

  // ─── Session history (for person device expanded rows) ───────
  const [historyData, setHistoryData] = useState({});
  const [historyLoading, setHistoryLoading] = useState({});

  // ─── Endpoint CRUD dialog ────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [dialogData, setDialogData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [devices, setDevices] = useState([]);

  // ─── Delete confirmation ─────────────────────────────────────
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ─── Provision dialogs ───────────────────────────────────────
  const [provisionLocationOpen, setProvisionLocationOpen] = useState(false);
  const [provisionPersonOpen, setProvisionPersonOpen] = useState(false);

  // ─── Feedback ────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ─── Data fetching ───────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const epParams = {};
      if (search) epParams.search = search;
      const ssParams = {};
      if (search) ssParams.search = search;
      if (activeOnly) ssParams.isActive = 'true';

      const [epRes, ssRes] = await Promise.all([
        telephonyApi.getSipEndpoints(epParams),
        telephonyApi.getSipSessions(ssParams),
      ]);

      const epList = epRes.data.endpoints || epRes.data.data || epRes.data || [];
      const ssList = ssRes.data.sessions || [];
      setEndpoints(Array.isArray(epList) ? epList : []);
      setSessions(Array.isArray(ssList) ? ssList : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load SIP devices');
    } finally {
      setLoading(false);
    }
  }, [search, activeOnly]);

  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await devicesApi.getAll();
      const list = data.devices || data.data || data || [];
      setDevices(Array.isArray(list) ? list : []);
    } catch { setDevices([]); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  // ─── Session history fetch ───────────────────────────────────
  const fetchHistory = useCallback(async (sessionId) => {
    if (historyData[sessionId]) return;
    setHistoryLoading(prev => ({ ...prev, [sessionId]: true }));
    try {
      const { data } = await telephonyApi.getSipSessionHistory(sessionId, { limit: 50 });
      setHistoryData(prev => ({ ...prev, [sessionId]: data.history || [] }));
    } catch {
      setHistoryData(prev => ({ ...prev, [sessionId]: [] }));
    } finally {
      setHistoryLoading(prev => ({ ...prev, [sessionId]: false }));
    }
  }, [historyData]);

  // ─── Normalize both datasets into unified rows ───────────────
  const unifiedRows = useMemo(() => {
    const rows = [];

    for (const ep of endpoints) {
      rows.push({
        id: `ep-${ep.id}`,
        sourceType: 'endpoint',
        sourceId: ep.id,
        extension: ep.extension,
        displayName: ep.displayName || ep.display_name || '-',
        type: 'location',
        status: ep.status === 'registered' ? 'registered' : 'unregistered',
        statusLabel: ep.status === 'registered' ? 'Registered' : 'Not Registered',
        statusBg: ep.status === 'registered' ? '#E8F5E9' : '#FFEBEE',
        statusColor: ep.status === 'registered' ? '#2E7D32' : '#C62828',
        deviceName: ep.deviceName || ep.device_name || null,
        personName: null,
        createdAt: ep.createdAt,
        lastRegisteredAt: ep.lastRegisteredAt,
        expiresAt: null,
        transport: ep.transport,
        context: ep.context,
        _raw: ep,
      });
    }

    for (const ss of sessions) {
      const si = getSessionStatusInfo(ss);
      rows.push({
        id: `ss-${ss.id}`,
        sourceType: 'session',
        sourceId: ss.id,
        extension: ss.extension,
        displayName: ss.personName || '-',
        type: 'person',
        status: si.label.toLowerCase(),
        statusLabel: si.label,
        statusBg: si.bg,
        statusColor: si.color,
        deviceName: ss.deviceName || null,
        personName: ss.personName || null,
        createdAt: ss.createdAt,
        lastRegisteredAt: ss.lastRegisteredAt,
        expiresAt: ss.expiresAt,
        transport: null,
        context: null,
        _raw: ss,
      });
    }

    // Apply type filter
    let filtered = rows;
    if (typeFilter === 'location') filtered = rows.filter(r => r.type === 'location');
    if (typeFilter === 'person') filtered = rows.filter(r => r.type === 'person');

    return filtered;
  }, [endpoints, sessions, typeFilter]);

  // ─── Sorting ─────────────────────────────────────────────────
  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const sortedRows = useMemo(() => {
    if (!orderBy) return unifiedRows;
    return [...unifiedRows].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (orderBy === 'extension') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else if (orderBy === 'createdAt' || orderBy === 'lastRegisteredAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [unifiedRows, orderBy, order]);

  // ─── Endpoint CRUD ───────────────────────────────────────────
  const openCreateDialog = () => {
    setDialogMode('create');
    setDialogData({ extension: '', displayName: '', password: '', transport: 'tls', context: 'caro-videophone', deviceId: '', notes: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (ep) => {
    setDialogMode('edit');
    setDialogData({
      id: ep.id, extension: ep.extension,
      displayName: ep.displayName || ep.display_name || '',
      password: ep.password || '', transport: ep.transport || 'tls',
      context: ep.context || 'caro-videophone',
      deviceId: ep.deviceId || ep.device_id || '', notes: ep.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!dialogData.displayName) return;
    try {
      setSaving(true);
      const payload = {
        extension: String(dialogData.extension),
        display_name: dialogData.displayName,
        password: dialogData.password || undefined,
        transport: dialogData.transport,
        context: dialogData.context,
        device_id: dialogData.deviceId || null,
        notes: dialogData.notes || null,
      };
      if (dialogMode === 'create') {
        await telephonyApi.createSipEndpoint(payload);
        setSnackbar({ open: true, message: 'SIP endpoint created', severity: 'success' });
      } else {
        await telephonyApi.updateSipEndpoint(dialogData.id, payload);
        setSnackbar({ open: true, message: 'SIP endpoint updated', severity: 'success' });
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      setDeleting(true);
      await telephonyApi.deleteSipEndpoint(deleteDialog.id);
      setSnackbar({ open: true, message: 'SIP endpoint deleted', severity: 'success' });
      setDeleteDialog(null);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to delete', severity: 'error' });
    } finally { setDeleting(false); }
  };

  const handleRegenerate = async (ep) => {
    try {
      await telephonyApi.regenerateSipCredentials(ep.id);
      setSnackbar({ open: true, message: 'Credentials regenerated', severity: 'success' });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to regenerate', severity: 'error' });
    }
  };

  const handleReloadAsterisk = async () => {
    try {
      const { data } = await telephonyApi.reloadAsterisk();
      setSnackbar({ open: true, message: data?.message || 'Asterisk will reload within 10 seconds', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to reload', severity: 'error' });
    }
  };

  const handleSyncCameras = async () => {
    try {
      const { data } = await telephonyApi.syncCameraPaths();
      setSnackbar({ open: true, message: data?.message || 'Camera paths synced', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to sync camera paths', severity: 'error' });
    }
  };

  const handlePrintLabel = async (ep) => {
    try {
      await generateSipDeviceLabel(ep);
      setSnackbar({ open: true, message: 'Label PDF generated', severity: 'success' });
    } catch (err) {
      console.error('Label generation error:', err);
      setSnackbar({ open: true, message: 'Failed to generate label', severity: 'error' });
    }
  };

  // ─── Row expand handler ──────────────────────────────────────
  const handleRowClick = (row) => {
    const isExpanded = expandedRow === row.id;
    if (isExpanded) {
      setExpandedRow(null);
    } else {
      setExpandedRow(row.id);
      if (row.sourceType === 'session') {
        fetchHistory(row.sourceId);
      }
    }
  };

  // ─── Counts for filter chips ─────────────────────────────────
  const epCount = endpoints.length;
  const ssCount = sessions.length;

  return (
    <>
      {/* ─── Toolbar ──────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search extension, name, device..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') fetchData(); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />

        {/* Type filter chips */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Chip
            label={`All (${epCount + ssCount})`}
            variant={typeFilter === 'all' ? 'filled' : 'outlined'}
            color={typeFilter === 'all' ? 'primary' : 'default'}
            size="small"
            onClick={() => setTypeFilter('all')}
          />
          <Chip
            icon={<LocationIcon sx={{ fontSize: '16px !important' }} />}
            label={`Location (${epCount})`}
            variant={typeFilter === 'location' ? 'filled' : 'outlined'}
            color={typeFilter === 'location' ? 'info' : 'default'}
            size="small"
            onClick={() => setTypeFilter('location')}
          />
          <Chip
            icon={<PersonIcon sx={{ fontSize: '16px !important' }} />}
            label={`Person (${ssCount})`}
            variant={typeFilter === 'person' ? 'filled' : 'outlined'}
            color={typeFilter === 'person' ? 'secondary' : 'default'}
            size="small"
            onClick={() => setTypeFilter('person')}
          />
        </Box>

        <FormControlLabel
          control={<Switch size="small" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />}
          label={<Typography variant="body2">Active only</Typography>}
        />

        {isAdmin && (
          <>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreateDialog}>
              Add SIP Endpoint
            </Button>
            <Button variant="contained" size="small" color="secondary" startIcon={<PhoneIcon />} onClick={() => setProvisionLocationOpen(true)}>
              Provision Location
            </Button>
            <Button variant="contained" size="small" color="info" startIcon={<PersonIcon />} onClick={() => setProvisionPersonOpen(true)}>
              Provision Person
            </Button>
            <Tooltip title="Reload Asterisk PJSIP config (auto-reloads within 10s)">
              <Button size="small" variant="outlined" color="warning" startIcon={<RefreshIcon />} onClick={handleReloadAsterisk}>
                Reload Asterisk
              </Button>
            </Tooltip>
            <Tooltip title="Re-register all camera streams in mediamtx (after mediamtx restart)">
              <Button size="small" variant="outlined" startIcon={<VideocamIcon />} onClick={handleSyncCameras}>
                Sync Cameras
              </Button>
            </Tooltip>
          </>
        )}
        <IconButton onClick={fetchData} size="small"><RefreshIcon /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ─── Table ────────────────────────────────────────────── */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sortDirection={orderBy === 'extension' ? order : false}>
                <TableSortLabel active={orderBy === 'extension'} direction={orderBy === 'extension' ? order : 'asc'} onClick={() => handleSort('extension')}>
                  Extension
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'displayName' ? order : false}>
                <TableSortLabel active={orderBy === 'displayName'} direction={orderBy === 'displayName' ? order : 'asc'} onClick={() => handleSort('displayName')}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Device</TableCell>
              <TableCell sortDirection={orderBy === 'lastRegisteredAt' ? order : false}>
                <TableSortLabel active={orderBy === 'lastRegisteredAt'} direction={orderBy === 'lastRegisteredAt' ? order : 'asc'} onClick={() => handleSort('lastRegisteredAt')}>
                  Last Registered
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No SIP devices found.
                </TableCell>
              </TableRow>
            ) : sortedRows.map((row) => {
              const isExpanded = expandedRow === row.id;
              return (
                <React.Fragment key={row.id}>
                  <TableRow
                    hover
                    onClick={() => handleRowClick(row)}
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                  >
                    <TableCell sx={{ width: 40, px: 1 }}>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem' }}>
                      {row.extension}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.displayName}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.type === 'location' ? (
                        <Chip icon={<LocationIcon sx={{ fontSize: '14px !important' }} />} label="Location" size="small" sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600, fontSize: '0.7rem' }} />
                      ) : (
                        <Chip icon={<PersonIcon sx={{ fontSize: '14px !important' }} />} label="Person" size="small" sx={{ bgcolor: '#F3E5F5', color: '#7B1FA2', fontWeight: 600, fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={row.statusLabel} size="small" sx={{ bgcolor: row.statusBg, color: row.statusColor, fontWeight: 600, fontSize: '0.75rem' }} />
                    </TableCell>
                    <TableCell>
                      {row.deviceName ? (
                        <Chip icon={<DeviceIcon fontSize="small" />} label={row.deviceName} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.secondary">&mdash;</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem' }}>
                      {row.lastRegisteredAt
                        ? new Date(row.lastRegisteredAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                        : '-'}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {row.sourceType === 'endpoint' && isAdmin && (
                        <>
                          <Tooltip title="Print label sticker">
                            <IconButton size="small" onClick={() => handlePrintLabel(row._raw)}>
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditDialog(row._raw)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Regenerate password">
                            <IconButton size="small" onClick={() => handleRegenerate(row._raw)} color="warning">
                              <RegenerateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setDeleteDialog(row._raw)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expandable detail row */}
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, px: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        {row.sourceType === 'endpoint' ? (
                          <DeviceConfigGuide endpoint={row._raw} />
                        ) : (
                          <Box sx={{ bgcolor: 'action.hover', px: 3, py: 0 }}>
                            <SipSessionDetail
                              session={row._raw}
                              history={historyData[row.sourceId] || []}
                              loading={historyLoading[row.sourceId]}
                            />
                          </Box>
                        )}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ─── Endpoint Create/Edit Dialog ──────────────────────── */}
      <EndpointDialog
        open={dialogOpen}
        mode={dialogMode}
        data={dialogData}
        devices={devices}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onChange={setDialogData}
        onSave={handleSave}
      />

      {/* ─── Delete Confirmation ──────────────────────────────── */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs">
        <DialogTitle>Delete SIP Endpoint?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove extension <strong>{deleteDialog?.extension}</strong> ({deleteDialog?.displayName || deleteDialog?.display_name}).
            The device will no longer be able to register with Asterisk.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Snackbar ─────────────────────────────────────────── */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ─── Provision Dialogs ────────────────────────────────── */}
      <ProvisionSipDeviceDialog
        open={provisionLocationOpen}
        onClose={() => setProvisionLocationOpen(false)}
        location={null}
        onSuccess={fetchData}
      />
      <ProvisionPersonDialog
        open={provisionPersonOpen}
        onClose={() => setProvisionPersonOpen(false)}
        onSuccess={fetchData}
      />
    </>
  );
}

// ═════════════════════════════════════════════════════════════════
// DEVICE CONFIG GUIDE (expanded row for Location/Endpoint devices)
// ═════════════════════════════════════════════════════════════════
function DeviceConfigGuide({ endpoint }) {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState('');

  const transport = (endpoint.transport || 'tls').toLowerCase();
  const port = transport === 'tls' ? ASTERISK_DEFAULTS.tlsPort : ASTERISK_DEFAULTS.udpPort;
  const hasCamera = endpoint.hasCamera && endpoint.deviceIp;
  const cameraEndpoint = endpoint.cameraEndpoint || `videophone-${endpoint.extension}`;

  const configFields = [
    { label: 'SIP Registrar / Server', value: ASTERISK_DEFAULTS.server, key: 'server' },
    { label: 'Port', value: String(port), key: 'port' },
    { label: 'Transport', value: transport.toUpperCase(), key: 'transport' },
    { label: 'SIP Username / Auth ID', value: endpoint.extension, key: 'username' },
    { label: 'Password / Secret', value: endpoint.password || '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022', key: 'password', sensitive: true },
    { label: 'Realm / Domain', value: ASTERISK_DEFAULTS.realm, key: 'realm' },
    { label: 'Caller ID', value: `"${endpoint.displayName || endpoint.display_name}" <${endpoint.extension}>`, key: 'callerid' },
    { label: 'Audio Codec', value: ASTERISK_DEFAULTS.codec, key: 'codec' },
  ];

  const cameraFields = hasCamera ? [
    { label: 'RTSP Username', value: endpoint.extension, key: 'rtsp-user' },
    { label: 'RTSP Password', value: endpoint.password || '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022', key: 'rtsp-pass', sensitive: true },
    { label: 'RTSP Stream Path', value: '/h264_stream', key: 'rtsp-path' },
    { label: 'Device IP', value: endpoint.deviceIp, key: 'device-ip' },
    { label: 'Camera Stream URL', value: `${CAMERA_STREAM_BASE}/${cameraEndpoint}/whep`, key: 'whep-url' },
  ] : [];

  const handleCopy = (value, key) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const renderFieldRow = (field) => (
    <Box
      key={field.key}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
    >
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 180, fontSize: '0.82rem' }}>
        {field.label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem', flex: 1, wordBreak: 'break-all' }}>
        {field.sensitive && !showPassword ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022' : field.value}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        {field.sensitive && (
          <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
          </IconButton>
        )}
        <Tooltip title={copied === field.key ? 'Copied!' : 'Copy'}>
          <IconButton size="small" onClick={() => handleCopy(field.sensitive ? (endpoint.password || '') : field.value, field.key)}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: 'action.hover', px: 3, py: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <ConfigIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
              Device SIP Configuration
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Enter these settings in the device's SIP / VoIP configuration panel.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {configFields.map(renderFieldRow)}
          </Box>

          {hasCamera && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, mb: 1.5 }}>
                <ConfigIcon fontSize="small" sx={{ color: '#1565C0' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
                  Camera / RTSP Configuration
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Set these RTSP credentials on the device to enable camera streaming. Uses the same credentials as SIP.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {cameraFields.map(renderFieldRow)}
              </Box>
            </>
          )}
        </Grid>

        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Registration Info
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ConfigDetail label="Status" value={
              endpoint.status === 'registered'
                ? <Chip label="Registered" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }} />
                : <Chip label="Not Registered" size="small" sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 600 }} />
            } />
            {endpoint.lastRegisteredAt && <ConfigDetail label="Last Registered" value={new Date(endpoint.lastRegisteredAt).toLocaleString()} />}
            {endpoint.deviceIp && <ConfigDetail label="Device IP" value={endpoint.deviceIp} mono />}
            {endpoint.userAgent && <ConfigDetail label="User Agent" value={endpoint.userAgent} mono />}
            {endpoint.notes && <ConfigDetail label="Notes" value={endpoint.notes} />}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Quick Setup Guide
          </Typography>
          <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" component="div" sx={{ lineHeight: 1.8 }}>
              1. Open the device web interface (admin panel)<br />
              2. Navigate to the SIP / VoIP settings section<br />
              3. Set <strong>SIP Proxy / Registrar</strong> to <code>{ASTERISK_DEFAULTS.server}:{port}</code><br />
              4. Set <strong>Username / Phone Number</strong> to <code>{endpoint.extension}</code><br />
              5. Set <strong>Auth ID</strong> to <code>{endpoint.extension}</code><br />
              6. Set <strong>Password</strong> to the password shown above<br />
              7. Set <strong>Transport</strong> to <strong>{transport.toUpperCase()}</strong><br />
              8. Set <strong>Audio Codec</strong> to <strong>G.711 {'\u03BC'}-law (PCMU)</strong><br />
              9. Apply settings and verify registration status
              {hasCamera && (<>
                <br /><br />
                <strong>Camera Setup:</strong><br />
                10. Navigate to the RTSP / Streaming settings<br />
                11. Set <strong>RTSP Username</strong> to <code>{endpoint.extension}</code><br />
                12. Set <strong>RTSP Password</strong> to the same password as SIP<br />
                13. Ensure <strong>H.264 stream</strong> is enabled
              </>)}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

function ConfigDetail({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 120, fontSize: '0.82rem' }}>{label}:</Typography>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? '0.8rem' : undefined }}>{value}</Typography>
      ) : value}
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════
// SIP SESSION DETAIL (expanded row for Person devices)
// ═════════════════════════════════════════════════════════════════
function SipSessionDetail({ session, history, loading }) {
  return (
    <Box sx={{ py: 2, px: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Session Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ConfigDetail label="Session ID" value={session.id} mono />
            <ConfigDetail label="Extension" value={session.extension} mono />
            <ConfigDetail label="Person" value={session.personName || '-'} />
            <ConfigDetail label="Device" value={session.deviceName || '-'} />
            <ConfigDetail label="Created" value={formatFullDateTime(session.createdAt)} />
            <ConfigDetail label="Expires" value={formatFullDateTime(session.expiresAt)} />
            <ConfigDetail label="Last Registered" value={formatFullDateTime(session.lastRegisteredAt)} />
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Registration History
          </Typography>
          {loading ? (
            <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No history recorded yet. State changes will appear here as the device registers and unregisters.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 300, overflowY: 'auto', pr: 1 }}>
              {history.map((entry) => {
                const cfg = sipEventConfig[entry.event] || { icon: <InfoIcon fontSize="small" />, color: '#616161', label: entry.event };
                const meta = entry.metadata;
                let detail = formatFullDateTime(entry.createdAt);
                if (entry.triggeredBy) detail += ` (by ${entry.triggeredBy})`;
                if (meta?.reason) detail += ` \u2014 ${meta.reason}`;
                if (meta?.ip) detail += ` from ${meta.ip}`;
                return (
                  <Box key={entry.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '50%',
                      bgcolor: `${cfg.color}15`, color: cfg.color, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25,
                    }}>
                      {cfg.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>{cfg.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{detail}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════
// ENDPOINT CREATE/EDIT DIALOG
// ═════════════════════════════════════════════════════════════════
function EndpointDialog({ open, mode, data, devices, saving, onClose, onChange, onSave }) {
  if (!data) return null;

  const deviceOptions = useMemo(() => {
    return devices.map((d) => {
      const name = d.friendlyName || d.name || `Device #${d.id}`;
      const ip = getDeviceLanIP(d) || '';
      const typeName = d.deviceTypeModel?.friendlyName || d.deviceTypeName || d.device_type_name || '';
      return { id: d.id, name, ip, typeName };
    });
  }, [devices]);

  const selectedDevice = deviceOptions.find((d) => d.id === data.deviceId) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Add SIP Endpoint' : `Edit SIP Endpoint \u2014 ${data.extension}`}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Context / Role</InputLabel>
            <Select value={data.context} label="Context / Role" onChange={(e) => onChange({ ...data, context: e.target.value })}>
              {CONTEXT_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Extension" value={data.extension} onChange={(e) => onChange({ ...data, extension: e.target.value })} size="small" sx={{ width: 160 }} required placeholder="e.g. 7001" helperText={mode === 'create' ? 'Leave empty to auto-assign' : undefined} disabled={mode === 'edit'} />
            <TextField label="Display Name" value={data.displayName} onChange={(e) => onChange({ ...data, displayName: e.target.value })} size="small" fullWidth required placeholder='e.g. "Front Door"' />
          </Box>

          <TextField
            label={mode === 'create' ? 'Password (leave blank to auto-generate)' : 'Password'}
            value={data.password}
            onChange={(e) => onChange({ ...data, password: e.target.value })}
            size="small" fullWidth
            placeholder={mode === 'create' ? 'Auto-generated if empty' : ''}
            helperText="SIP authentication password for this device"
          />

          <FormControl fullWidth size="small">
            <InputLabel>Transport</InputLabel>
            <Select value={data.transport} label="Transport" onChange={(e) => onChange({ ...data, transport: e.target.value })}>
              {TRANSPORT_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            options={deviceOptions}
            value={selectedDevice}
            onChange={(_, newValue) => onChange({ ...data, deviceId: newValue?.id || '' })}
            getOptionLabel={(option) => option.name}
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.toLowerCase();
              if (!q) return options;
              return options.filter((o) => o.name.toLowerCase().includes(q) || o.ip.toLowerCase().includes(q) || o.typeName.toLowerCase().includes(q) || String(o.id).includes(q));
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{[option.typeName, option.ip].filter(Boolean).join(' \u00B7 ') || `ID: ${option.id}`}</Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Link to Device (Optional)" placeholder="Search by name, IP, or type..." />}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            clearOnBlur clearOnEscape
          />

          <TextField label="Notes (Optional)" value={data.notes} onChange={(e) => onChange({ ...data, notes: e.target.value })} size="small" fullWidth multiline rows={2} placeholder="Location, model, serial number..." />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={onSave} variant="contained" disabled={saving || !data.displayName}>
          {saving ? <CircularProgress size={20} /> : mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
